import { SeverityLevel } from '@microsoft/applicationinsights-web';
import {
  CreateOrderApprovalRequest,
  GetOrderApprovalsRequest,
  RejectOrderApprovalRequest,
} from '../../api/models/order-approval.models';
import OrderApprovalService from '../../api/services/order-approval.service';
import { useAppInsightsLogger } from '../../logging/AppInsightsLogger';
import { Resolution, UserActivityAction, UserActivityActionSummary, UserActivityPageName } from '../../models';
import { logUserActivity } from '../common';
import { globalSlice } from '../common/global.slice';
import { AppDispatch, AppThunk, RootState } from '../store';
import { orderApprovalSlice } from './order-approval.slice';

const orderApprovalService = OrderApprovalService.getInstance();
const appInsightsLogger = useAppInsightsLogger();

export const getOrderApproval =
  (orderEntryHeaderId: string): AppThunk =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      if (!getState().orderApproval.isLoadingApprovals) {
        dispatch(orderApprovalSlice.actions.setIsLoadingApprovalInfo(true));
      }

      const { data } = await orderApprovalService.getOrderApproval(orderEntryHeaderId);

      if (data.IsSuccess) {
        dispatch(orderApprovalSlice.actions.setApprovalInfo(data.ResultObject));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    } finally {
      dispatch(orderApprovalSlice.actions.setIsLoadingApprovalInfo(false));
    }
  };

export const getOrderApprovals =
  (customerIds: string[]): AppThunk =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      if (!getState().orderApproval.isLoadingApprovals) {
        dispatch(orderApprovalSlice.actions.setIsLoadingApprovals(true));
      }

      const request: GetOrderApprovalsRequest = {
        CustomerIds: customerIds,
      };

      const { data } = await orderApprovalService.getOrderApprovals(request);

      if (data.IsSuccess) {
        dispatch(orderApprovalSlice.actions.setOrderApprovals(data.ResultObject));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    } finally {
      dispatch(orderApprovalSlice.actions.setIsLoadingApprovals(false));
    }
  };

export const createOrderApproval =
  (orderEntryHeaderId: string, resolution: Resolution): AppThunk<Promise<boolean>> =>
  async (dispatch: AppDispatch): Promise<boolean> => {
    let result = false;

    try {
      const request: CreateOrderApprovalRequest = {
        OrderEntryHeaderId: orderEntryHeaderId,
      };

      const { data } = await orderApprovalService.createOrderApproval(request);

      if (data.IsSuccess) {
        dispatch(orderApprovalSlice.actions.setApprovalInfo(data.ResultObject));
        dispatch(
          logUserActivity({
            resolution,
            action: UserActivityAction.SendOrderForApproval,
            pageName: UserActivityPageName.OrderReview,
            actionSummary: UserActivityActionSummary.SentOrderForApproval,
          })
        );
        result = true;
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    }

    return result;
  };

export const rejectOrderApproval =
  (orderEntryHeaderId: string, resolution: Resolution, orderApprovalComment?: string): AppThunk<Promise<boolean>> =>
  async (dispatch: AppDispatch): Promise<boolean> => {
    let result = false;

    try {
      const request: RejectOrderApprovalRequest = {
        OrderEntryHeaderId: orderEntryHeaderId,
        OrderApprovalComment: orderApprovalComment ?? '',
      };

      const { data } = await orderApprovalService.rejectOrderApproval(request);

      if (data.IsSuccess) {
        dispatch(orderApprovalSlice.actions.setApprovalInfo(data.ResultObject));
        dispatch(
          logUserActivity({
            resolution,
            action: UserActivityAction.RejectOrder,
            pageName: UserActivityPageName.OrderReview,
            actionSummary: UserActivityActionSummary.RejectedOrder,
          })
        );
        result = true;
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    }

    return result;
  };
